import * as React from "react";
import {
  Card,
  Subtitle1,
  Subtitle2,
  Text,
  Input,
  Button,
  Checkbox,
  Spinner,
  MessageBar,
  MessageBarBody,
  Field,
} from "@fluentui/react-components";
import { Save20Regular } from "@fluentui/react-icons";
import {
  sepaCreditorConfigLaden,
  sepaCreditorConfigSpeichern,
  type SepaCreditorConfig,
} from "../../services/sepaCreditorService";
import { glaeubigerIdGueltig, ibanGueltig, ibanNormalisieren } from "../../utils/iban";

/**
 * Die eigenen Bank- und Gläubigerdaten des Maklerbüros.
 *
 * **Warum es diese Maske gibt.** Der SEPA-Export verweigert ohne sie den
 * Dienst — richtigerweise: Im Altsystem standen Kontoinhaber, IBAN und BIC als
 * Literale im VBA-Code, und als Gläubiger-ID der Platzhalter
 * `DE00ZZZ00099999999`. Ein zweites Maklerbüro oder eine zweite Bankverbindung
 * war damit nicht darstellbar. Die Werte lagen seit der Umstellung in der
 * Datenbank, nur konnte sie niemand eintragen; auf PROD war die Tabelle leer
 * und der Export damit unbenutzbar.
 *
 * **Geprüft wird beim Tippen, nicht erst bei der Bank.** IBAN und Gläubiger-ID
 * tragen Prüfziffern. Eine Bank weist eine fehlerhafte Einreichungsdatei immer
 * VOLLSTÄNDIG zurück, nicht den einzelnen Satz — ein Zahlendreher hier legt
 * also einen ganzen Lauf still.
 */

/** Die Vorlauftage sind Kalendertage, nicht Bankarbeitstage. */
const VORLAUF: Array<{
  feld: "creditTransferLeadDays" | "directDebitLeadDaysFirst" | "directDebitLeadDaysRecurrent";
  titel: string;
  hilfe: string;
}> = [
  {
    feld: "creditTransferLeadDays",
    titel: "Überweisung",
    hilfe: "Tage bis zum gewünschten Ausführungstag.",
  },
  {
    feld: "directDebitLeadDaysFirst",
    titel: "Lastschrift, erstmalig",
    hilfe: "Erst- und Einmallastschrift (FRST/OOFF) — üblich sind 5 Tage.",
  },
  {
    feld: "directDebitLeadDaysRecurrent",
    titel: "Lastschrift, wiederkehrend",
    hilfe: "Folge- und letzte Lastschrift (RCUR/FNAL) — üblich sind 2 Tage.",
  },
];

const LEER: SepaCreditorConfig = {
  accountHolderName: "",
  iban: "",
  bic: "",
  initiatingPartyName: "",
  creditorId: "",
  creditTransferVersion: "pain.001.001.09",
  directDebitVersion: "pain.008.001.08",
  creditTransferLeadDays: 1,
  directDebitLeadDaysFirst: 5,
  directDebitLeadDaysRecurrent: 2,
  batchBooking: false,
};

export const SepaCreditorSection: React.FC = () => {
  const [daten, setDaten] = React.useState<SepaCreditorConfig>(LEER);
  const [laedt, setLaedt] = React.useState(true);
  const [speichert, setSpeichert] = React.useState(false);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [erfolg, setErfolg] = React.useState(false);
  const [neuAnzulegen, setNeuAnzulegen] = React.useState(false);

  React.useEffect(() => {
    void (async () => {
      try {
        const vorhanden = await sepaCreditorConfigLaden();
        setNeuAnzulegen(!vorhanden);
        if (vorhanden) setDaten({ ...LEER, ...vorhanden });
      } catch (e) {
        setFehler(e instanceof Error ? e.message : String(e));
      } finally {
        setLaedt(false);
      }
    })();
  }, []);

  const setzen = <K extends keyof SepaCreditorConfig>(feld: K, wert: SepaCreditorConfig[K]) => {
    setDaten((alt) => ({ ...alt, [feld]: wert }));
    setErfolg(false);
  };

  const ibanFehler =
    daten.iban.trim() && !ibanGueltig(daten.iban) ? "Die Prüfziffern stimmen nicht." : undefined;
  const glaeubigerFehler =
    String(daten.creditorId ?? "").trim() && !glaeubigerIdGueltig(daten.creditorId)
      ? "Die Prüfziffern stimmen nicht."
      : undefined;
  const nameFehlt = !daten.accountHolderName.trim();

  // Gespeichert wird nur, was auch durchgeht: Die Bank weist eine fehlerhafte
  // Datei vollständig zurück, nicht den einzelnen Satz.
  const speicherbar =
    !nameFehlt && ibanGueltig(daten.iban) && !glaeubigerFehler && !speichert;

  const speichern = async () => {
    setSpeichert(true);
    setFehler(null);
    try {
      const gespeichert = await sepaCreditorConfigSpeichern({
        ...daten,
        iban: ibanNormalisieren(daten.iban),
        bic: String(daten.bic ?? "").trim().toUpperCase() || null,
        creditorId: ibanNormalisieren(daten.creditorId) || null,
        initiatingPartyName: String(daten.initiatingPartyName ?? "").trim() || null,
      });
      setDaten({ ...LEER, ...gespeichert });
      setNeuAnzulegen(false);
      setErfolg(true);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  };

  if (laedt) {
    return (
      <Card style={{ padding: 24 }}>
        <Spinner label="Bankdaten werden geladen …" />
      </Card>
    );
  }

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Subtitle1>Eigene Bankdaten für SEPA</Subtitle1>
        <Text block style={{ marginTop: 6, color: "#6b5a66" }}>
          Diese Angaben stehen in jeder Einreichungsdatei, die an die Bank geht. Ohne
          sie lässt sich kein Zahlungsverkehr exportieren.
        </Text>
      </div>

      {neuAnzulegen && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Für diesen Mandanten sind noch keine Bankdaten hinterlegt — der SEPA-Export
            ist deshalb noch nicht einsatzbereit.
          </MessageBarBody>
        </MessageBar>
      )}
      {fehler && (
        <MessageBar intent="error">
          <MessageBarBody>{fehler}</MessageBarBody>
        </MessageBar>
      )}
      {erfolg && (
        <MessageBar intent="success">
          <MessageBarBody>Die Bankdaten sind gespeichert.</MessageBarBody>
        </MessageBar>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        <Field
          label="Kontoinhaber"
          required
          validationState={nameFehlt ? "warning" : "none"}
          validationMessage={nameFehlt ? "Wird für jede Einreichung gebraucht." : undefined}
          hint="So, wie der Name bei der Bank geführt wird."
        >
          <Input
            value={daten.accountHolderName}
            maxLength={70}
            onChange={(_, d) => setzen("accountHolderName", d.value)}
          />
        </Field>

        <Field
          label="Abweichender Auftraggeber"
          hint="Nur ausfüllen, wenn er vom Kontoinhaber abweicht."
        >
          <Input
            value={String(daten.initiatingPartyName ?? "")}
            maxLength={70}
            onChange={(_, d) => setzen("initiatingPartyName", d.value)}
          />
        </Field>

        <Field
          label="IBAN"
          required
          validationState={ibanFehler ? "error" : "none"}
          validationMessage={ibanFehler}
        >
          <Input
            value={daten.iban}
            maxLength={42}
            onChange={(_, d) => setzen("iban", d.value)}
          />
        </Field>

        <Field label="BIC" hint="Bei deutschen Konten meist entbehrlich.">
          <Input
            value={String(daten.bic ?? "")}
            maxLength={11}
            onChange={(_, d) => setzen("bic", d.value)}
          />
        </Field>

        <Field
          label="Gläubiger-Identifikationsnummer"
          validationState={glaeubigerFehler ? "error" : "none"}
          validationMessage={glaeubigerFehler}
          hint="Nur für Lastschriften nötig. Wird von der Bundesbank vergeben."
        >
          <Input
            value={String(daten.creditorId ?? "")}
            maxLength={35}
            onChange={(_, d) => setzen("creditorId", d.value)}
          />
        </Field>
      </div>

      <div>
        <Subtitle2>Vorlauffristen</Subtitle2>
        <Text block style={{ marginTop: 4, marginBottom: 12, color: "#6b5a66" }}>
          Kalendertage zwischen Einreichung und Ausführung. Das Altsystem setzte
          beides stets auf heute — dass Banken das stillschweigend korrigieren, ist
          kein Verlass.
        </Text>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {VORLAUF.map((v) => (
            <Field key={v.feld} label={v.titel} hint={v.hilfe}>
              <Input
                type="number"
                min={0}
                max={60}
                value={String(daten[v.feld] ?? 0)}
                onChange={(_, d) => setzen(v.feld, Math.max(0, Number(d.value) || 0))}
              />
            </Field>
          ))}
        </div>
      </div>

      <div>
        <Subtitle2>Format</Subtitle2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 12 }}>
          <Field label="Schema Überweisung" hint="Nur ändern, wenn die Bank es verlangt.">
            <Input
              value={String(daten.creditTransferVersion ?? "")}
              maxLength={32}
              onChange={(_, d) => setzen("creditTransferVersion", d.value)}
            />
          </Field>
          <Field label="Schema Lastschrift" hint="Nur ändern, wenn die Bank es verlangt.">
            <Input
              value={String(daten.directDebitVersion ?? "")}
              maxLength={32}
              onChange={(_, d) => setzen("directDebitVersion", d.value)}
            />
          </Field>
        </div>
        <Checkbox
          style={{ marginTop: 12 }}
          checked={!!daten.batchBooking}
          onChange={(_, d) => setzen("batchBooking", !!d.checked)}
          label="Sammelbuchung — auf dem eigenen Kontoauszug erscheint eine Summe statt jedes Einzelpostens"
        />
      </div>

      <div>
        <Button
          appearance="primary"
          icon={<Save20Regular />}
          disabled={!speicherbar}
          onClick={() => void speichern()}
        >
          {speichert ? "Wird gespeichert …" : "Bankdaten speichern"}
        </Button>
      </div>
    </Card>
  );
};
